import { createRootRoute, createRoute, Navigate } from "@tanstack/react-router";
import { lazy, Suspense } from "react";

import { DataPath } from "../screens/DataPath";
import { SiteNow } from "../screens/SiteNow";
import { EdgeLayout } from "../shell/EdgeLayout";
import { validateTimeSearch } from "./search";

/**
 * Edge route tree (X3). A site-scoped subset of the cloud routes, intended for
 * the self-contained LAN/edge artifact that runs on-site with no fleet views.
 *
 * The edge node's VITE_EDGE_SITE env var names the site; every route binds to
 * that single site. Fleet/scope/admin screens are excluded — the edge device is
 * at one plant, not many.
 *
 * Routes are flat (no /t/$tenant/s/$scope nesting) because there is exactly one
 * tenant and one site on an edge node.
 */

const edgeSite = import.meta.env.VITE_EDGE_SITE ?? "local";

// Code-split elkjs and uPlot, same as the cloud routes.
const PlantSchematic = lazy(() =>
  import("../screens/PlantSchematic").then((m) => ({ default: m.PlantSchematic })),
);
const SensorView = lazy(() =>
  import("../screens/SensorView").then((m) => ({ default: m.SensorView })),
);

export const edgeRootRoute = createRootRoute({ component: EdgeLayout });

// Landing: redirect straight to Site: Now.
const edgeIndexRoute = createRoute({
  getParentRoute: () => edgeRootRoute,
  path: "/",
  component: () => <Navigate to="/now" search={{ period: "today", mode: "live" }} />,
});

// P2 — Site: Now
const edgeNowRoute = createRoute({
  getParentRoute: () => edgeRootRoute,
  path: "/now",
  validateSearch: validateTimeSearch,
  component: () => <SiteNow site={edgeSite} />,
});

// P9 — Data path
const edgeDataPathRoute = createRoute({
  getParentRoute: () => edgeRootRoute,
  path: "/datapath",
  component: () => <DataPath site={edgeSite} />,
});

// P8 — Plant schematic
const edgeSchematicRoute = createRoute({
  getParentRoute: () => edgeRootRoute,
  path: "/schematic",
  component: () => (
    <Suspense fallback={<p className="text-text-muted">Loading schematic…</p>}>
      <PlantSchematic site={edgeSite} />
    </Suspense>
  ),
});

// P4 — Meters & sensors
const edgeSensorsRoute = createRoute({
  getParentRoute: () => edgeRootRoute,
  path: "/sensors",
  component: () => (
    <Suspense fallback={<p className="text-text-muted">Loading sensors…</p>}>
      <SensorView site={edgeSite} />
    </Suspense>
  ),
});

export const edgeRouteTree = edgeRootRoute.addChildren([
  edgeIndexRoute,
  edgeNowRoute,
  edgeDataPathRoute,
  edgeSchematicRoute,
  edgeSensorsRoute,
]);
